/* ==========================================================================
   GR- Brief Form — Growthly
   assets/gr-brief-form.js

   Steps, validation, and the three send actions.

   The form works without this file: every field is inside a real Shopify
   contact form, so submitting still posts. What is lost is the stepping, so
   all steps are visible at once — a degraded but usable page, never a broken
   one.
   ========================================================================== */
(() => {
  'use strict';

  if (customElements.get('gr-brief')) return;

  class GRBrief extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-brief-form]') || this.querySelector('form');
      if (!this.form) return;

      this.steps = [...this.querySelectorAll('[data-step]')];
      this.index = 0;

      // Index the children of each step so the stylesheet can cascade them in.
      this.steps.forEach((step) => {
        [...step.children].forEach((el, i) => el.style.setProperty('--gr-i', String(i)));
      });

      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.finish = this.querySelector('[data-finish]');
      this.bodyInput = this.querySelector('[data-body]');

      this.bindNav();
      this.bindRanges();
      this.bindActions();
      this.bindLiveValidation();
      this.bindWebsite();
      this.bindChoiceGroups();
      this.bindMoney();
      this.show(0, true);

      /* The bar starts at scaleX(0) so it can grow into step one rather than
         appearing already filled. Painting the real value on the next frame
         gives the browser a frame to commit the zero state, which is what
         makes the first step animate at all. */
      requestAnimationFrame(() => this.updateProgress());

      /* Shopify returns to the page with #contact_form after a post. Without
         this the visitor lands back on step one and cannot see the success
         message, which sits above the steps. */
      if (location.hash.indexOf('contact') !== -1) {
        const done = this.querySelector('.gr-brief__done, .gr-brief__error');
        if (done) done.scrollIntoView({ block: 'center' });
      }
    }

    /* ---- steps ------------------------------------------------------------- */
    bindNav() {
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => {
        if (!this.validate(this.index)) return;
        this.show(this.index + 1);
      });
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.show(this.index - 1));

      /* Enter should advance, not submit — a half-filled brief posted by an
         accidental keypress is worse than no brief. Textareas keep their
         normal newline behaviour. */
      this.form.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        if (e.target.tagName === 'TEXTAREA') return;
        if (this.index < this.steps.length - 1) {
          e.preventDefault();
          if (this.validate(this.index)) this.show(this.index + 1);
        }
      });

      // Block a submit from any step but the last, for the same reason.
      this.form.addEventListener('submit', (e) => {
        if (this.index < this.steps.length - 1) { e.preventDefault(); return; }
        if (!this.validate(this.index)) { e.preventDefault(); return; }
        this.buildBody();
      });
    }

    show(i, instant) {
      const n = this.steps.length;
      if (!n) return;
      this.index = Math.max(0, Math.min(n - 1, i));

      this.steps.forEach((el, k) => {
        const on = k === this.index;
        el.hidden = !on;
        el.classList.toggle('is-active', on);
        if (on && !instant) {
          // Restart the entry animation: removing and re-adding in the same
          // frame is ignored, so a reflow is forced between the two.
          el.classList.remove('is-entering');
          void el.offsetWidth;
          el.classList.add('is-entering');
        }
      });

      const last = this.index === n - 1;
      if (this.prevBtn) this.prevBtn.hidden = this.index === 0;
      if (this.nextBtn) this.nextBtn.hidden = last;
      if (this.finish) this.finish.hidden = !last;

      this.updateProgress();

      if (!instant) {
        const top = this.getBoundingClientRect().top + window.scrollY - 90;
        window.scrollTo({ top, behavior: 'smooth' });
        // Move focus into the new step so a keyboard user is not left behind.
        const first = this.steps[this.index].querySelector('input, select, textarea');
        if (first) setTimeout(() => first.focus({ preventScroll: true }), 260);
      }
    }

    updateProgress() {
      const n = this.steps.length;
      const bar = this.querySelector('[data-bar]');
      const now = this.querySelector('[data-step-now]');
      const name = this.querySelector('[data-step-name]');

      if (bar) bar.style.setProperty('--gr-bf-p', String((this.index + 1) / n));
      if (now) now.textContent = String(this.index + 1);
      if (name) name.textContent = this.steps[this.index].dataset.stepTitle || '';
    }

    /* ---- validation --------------------------------------------------------
       Native constraint validation reports one field at a time and scrolls to
       hidden inputs, which breaks badly in a stepped form. This checks only
       the visible step and shows every problem at once.
       ---------------------------------------------------------------------- */
    validate(i) {
      const step = this.steps[i];
      if (!step) return true;

      const msg = this.dataset.requiredMsg || 'Please complete this field.';
      let ok = true;
      let firstBad = null;

      step.querySelectorAll('.gr-brief__field').forEach((field) => {
        field.classList.remove('has-error');
        const out = field.querySelector('[data-msg]');
        if (out) out.textContent = '';

        /* Choice groups have no single required element — and their inputs
           are deliberately unnamed, so the browser cannot validate them
           either. Both radio and checkbox groups are checked here. */
        const group = field.querySelector('[data-group-required]');
        if (group) {
          const any = field.querySelector('input:checked');
          if (!any) {
            field.classList.add('has-error');
            if (out) out.textContent = msg;
            ok = false;
            firstBad = firstBad || field;
          }
          return;
        }

        field.querySelectorAll('[required]').forEach((el) => {
          const empty = el.type === 'checkbox' ? !el.checked : !el.value.trim();
          const bad = empty || (el.checkValidity && !el.checkValidity());
          if (!bad) return;
          field.classList.add('has-error');
          if (out) out.textContent = el.validationMessage || msg;
          ok = false;
          firstBad = firstBad || field;
        });
      });

      if (firstBad) firstBad.scrollIntoView({ block: 'center', behavior: 'smooth' });
      return ok;
    }

    bindLiveValidation() {
      // Clear an error as soon as the visitor fixes it, rather than making
      // them press Continue again to find out.
      this.form.addEventListener('input', (e) => {
        const field = e.target.closest('.gr-brief__field');
        if (field) field.classList.remove('has-error');
      });
      this.form.addEventListener('change', (e) => {
        const field = e.target.closest('.gr-brief__field');
        if (field) field.classList.remove('has-error');
      });
    }

    /* ---- choice groups -----------------------------------------------------
       Radios are unnamed on purpose: a named field inside a Shopify contact
       form is posted and validated, and an unknown name comes back as
       "<question> is invalid". Without a shared name the browser will not
       make them mutually exclusive, so that is done here.
       ---------------------------------------------------------------------- */
    bindChoiceGroups() {
      this.querySelectorAll('[data-multi="false"]').forEach((wrap) => {
        wrap.addEventListener('change', (e) => {
          const hit = e.target;
          if (!hit || hit.type !== 'radio') return;
          wrap.querySelectorAll('input[type="radio"]').forEach((el) => {
            if (el !== hit) el.checked = false;
          });
        });
      });
    }

    /* ---- website ------------------------------------------------------------
       "Not yet" hides the field rather than leaving an empty box the visitor
       has to ignore. The input is also cleared, so a value typed before
       switching cannot be submitted invisibly.
       ---------------------------------------------------------------------- */
    bindWebsite() {
      this.querySelectorAll('[data-site-toggle]').forEach((radio) => {
        const id = radio.dataset.siteToggle;
        const input = this.querySelector(`[data-site-input="${id}"]`);
        if (!input) return;

        const sync = () => {
          const has = this.querySelector(`[data-site-toggle="${id}"]:checked`);
          const on = !has || has.value === 'yes';
          input.hidden = !on;
          if (!on) input.value = '';
        };
        radio.addEventListener('change', sync);
        sync();
      });
    }

    /* ---- amount + currency -------------------------------------------------
       Digits are grouped as the visitor types. The caret is not restored,
       because this field is always typed left to right and the extra
       complexity of caret maths is not worth it here.
       ---------------------------------------------------------------------- */
    bindMoney() {
      this.querySelectorAll('[data-money-amount]').forEach((el) => {
        el.addEventListener('input', () => {
          const digits = el.value.replace(/[^\d]/g, '');
          el.value = digits ? Number(digits).toLocaleString() : '';
        });
      });
    }

    /* ---- ranges ------------------------------------------------------------ */
    bindRanges() {
      this.querySelectorAll('.gr-brief__range').forEach((el) => {
        const out = this.querySelector(`[data-output-for="${el.id}"]`);
        const paint = () => {
          if (!out) return;
          const pre = el.dataset.prefix || '';
          const suf = el.dataset.suffix || '';
          // Thousands separators: a raw 150000 is hard to read at a glance.
          out.textContent = pre + Number(el.value).toLocaleString() + suf;
        };
        el.addEventListener('input', paint);
        paint();
      });
    }

    /* ---- collecting answers ------------------------------------------------
       Every question carries data-q, so the label travels with the value and
       nothing has to be matched up by index.
       ---------------------------------------------------------------------- */
    collect() {
      const out = [];

      this.querySelectorAll('[data-q]').forEach((el) => {
        const q = el.dataset.q;
        if (!q) return;

        // Choice groups are a wrapper, not an input.
        if (el.classList.contains('gr-brief__choices')) {
          const picked = [...el.querySelectorAll('input:checked')].map((i) => i.value);
          if (picked.length) out.push([q, picked.join(', ')]);
          return;
        }

        if (el.type === 'checkbox') {
          out.push([q, el.checked ? 'Yes' : 'No']);
          return;
        }

        // Amount + currency wrapper.
        if (el.hasAttribute('data-money')) {
          const amount = el.querySelector('[data-money-amount]');
          const cur = el.querySelector('[data-money-cur]');
          const v = amount && amount.value.trim();
          if (v) out.push([q, `${v} ${cur ? cur.value : ''}`.trim()]);
          return;
        }

        if (el.type === 'range') {
          const pre = el.dataset.prefix || '';
          const suf = el.dataset.suffix || '';
          // A currency picked beside the slider belongs to this answer.
          const chip = this.querySelector(`[data-cur-group="${el.id}"] input:checked`);
          const cur = chip ? ' ' + chip.value : '';
          out.push([q, pre + Number(el.value).toLocaleString() + suf + cur]);
          return;
        }

        // Website field switched to "Not yet".
        if (el.hasAttribute('data-site-input')) {
          if (el.hidden) { out.push([q, 'No website yet']); return; }
          const v = (el.value || '').trim();
          if (v) out.push([q, v]);
          return;
        }

        const v = (el.value || '').trim();
        if (v) out.push([q, v]);
      });

      return out;
    }

    /* Shopify's contact form only emails a fixed set of field names and drops
       everything else without warning. Folding the whole brief into
       contact[body] is the only reliable way to receive it. */
    buildBody() {
      if (!this.bodyInput) return;
      this.bodyInput.value = this.collect()
        .map(([q, a]) => `${q}\n${a}`)
        .join('\n\n');
    }

    /* ---- send actions ------------------------------------------------------ */
    bindActions() {
      const wa = this.querySelector('[data-send-wa]');
      if (wa) wa.addEventListener('click', () => {
        if (!this.validate(this.index)) return;

        const num = this.dataset.whatsapp;
        if (!num) return;

        const intro = this.dataset.waIntro || 'New project brief';
        const lines = this.collect().map(([q, a]) => `*${q}*\n${a}`);
        const text = [intro, '', ...lines].join('\n');

        /* encodeURIComponent, not encodeURI: the latter leaves &, # and +
           intact, and any of them truncates the message at that character. */
        window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, '_blank', 'noopener');
      });

      const pdf = this.querySelector('[data-print]');
      if (pdf) pdf.addEventListener('click', () => {
        this.buildPrint();
        // A frame between filling the summary and printing, or the dialog can
        // capture the page before the new nodes have been painted.
        requestAnimationFrame(() => requestAnimationFrame(() => window.print()));
      });
    }

    buildPrint() {
      const list = this.querySelector('[data-print-list]');
      const date = this.querySelector('[data-print-date]');
      if (date) date.textContent = new Date().toLocaleDateString();
      if (!list) return;

      list.textContent = '';
      this.collect().forEach(([q, a]) => {
        const dt = document.createElement('dt');
        dt.textContent = q;
        const dd = document.createElement('dd');
        // textContent, never innerHTML: the values are visitor input.
        dd.textContent = a;
        list.appendChild(dt);
        list.appendChild(dd);
      });
    }
  }

  customElements.define('gr-brief', GRBrief);
})();
